import { useMemo, useRef, useState } from 'react';
import type { OrderRecord } from '@adiox/api';
import {
  ConfirmDialog,
  Modal,
  formatServerDateTime,
  useDisplayZone,
  useMoneyFormat,
  useSession,
  usePrefsValue,
} from '@adiox/ui';
import { ContextMenu, type MenuItem } from '../chart/ChartOverlays';
import { TickChart } from '../chart/TickChart';
import { useSymbols } from '../market/useSymbols';
import { useStream } from '../stream/stream';
import type { Position } from '../stream/accountModel';
import { useQuoteSubscription } from '../stream/useQuoteSubscription';
import { useWorkspace } from '../workspace/workspaceStore';
import { LEVEL_UNITS, UNIT_LABELS, levelPrice, levelText, type LevelKind, type LevelUnit } from './levels';
import { bulkActions, oppositesOf, type BulkAction } from './bulkActions';
import type { Outcome } from './tradeClient';
import {
  DEFAULT_SORT,
  nextSort,
  pendingDistance,
  positionTotals,
  resultAt,
  sortPositions,
  type PositionSort,
  type PositionSortKey,
} from './tradeMath';
import { normalizeVolume, sideOfOrderType, volumeProblem, type OrderType, type Side } from './tradeRules';
import { useMoneyPerLot } from './useMoneyPerLot';
import { useActiveLogin, useTradeClient } from './useTrading';
import { useLayout } from '../shell/layout';
import { MobileTradeView } from './MobileTradeView';

type OrderRow = OrderRecord;
const selectOrders = (orders: Record<number, OrderRecord>) =>
  Object.values(orders)
    .filter((o) => o.status === 'Placed')
    .sort((a, b) => a.ticket - b.ticket);

const tone = (v: number | null) => (v === null ? '' : v > 0 ? 'text-up' : v < 0 ? 'text-down' : '');

export type Editing = { kind: 'position'; row: Position } | { kind: 'order'; row: OrderRow } | null;
type Notice = { tone: 'ok' | 'error'; text: string } | null;

const th = 'px-2 py-1 text-left text-xs font-normal uppercase tracking-wide text-muted';
const td = 'px-2 py-1 whitespace-nowrap';
const small =
  'rounded border border-line px-2 py-0.5 text-xs hover:border-accent disabled:cursor-not-allowed disabled:opacity-40';
/** No width utility, so a caller can pick one (`flex-1`, `w-20`, …) without fighting `input`'s own `w-full`. */
const fieldBase =
  'rounded-md border border-line bg-panel2 px-2 py-1 text-sm outline-none focus:border-accent num';
const input = `w-full ${fieldBase}`;

const POSITION_COLUMNS: { key: PositionSortKey; label: string; right?: boolean }[] = [
  { key: 'ticket', label: 'Ticket' },
  { key: 'timeOpen', label: 'Time' },
  { key: 'symbol', label: 'Symbol' },
  { key: 'side', label: 'Type' },
  { key: 'volume', label: 'Volume', right: true },
  { key: 'priceOpen', label: 'Open price', right: true },
  { key: 'stopLoss', label: 'S/L', right: true },
  { key: 'takeProfit', label: 'T/P', right: true },
  { key: 'price', label: 'Price', right: true },
  { key: 'commission', label: 'Commission', right: true },
  { key: 'swap', label: 'Swap', right: true },
  { key: 'profit', label: 'Profit', right: true },
  { key: 'comment', label: 'Comment' },
];

/**
 * Open positions and pending orders, live from the account stream: sortable, with totals, modify (double-click), a context
 * menu (right-click), partial close, and closing all, the profitable, the losing or one symbol's positions.
 */
export function TradePanel() {
  const login = useActiveLogin();
  const client = useTradeClient();
  const account = useStream((s) => (login === undefined ? undefined : s.accounts[login]));
  const hubState = useStream((s) => s.hubState);
  const quotes = useStream((s) => s.quotes);
  const zone = useDisplayZone();
  const fmt = useMoneyFormat();
  const { mobile } = useLayout();
  const signed = (v: number) => `${v > 0 ? '+' : ''}${fmt(v)}`;
  const confirmClose = usePrefsValue((s) => s.data.confirmClose === true);
  const symbols = useSymbols();
  const select = useWorkspace((s) => s.select);
  const [editing, setEditing] = useState<Editing>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const running = useRef(false);
  const [sort, setSort] = useState<PositionSort>(DEFAULT_SORT);
  const [bulk, setBulk] = useState<BulkAction | null>(null);
  const [closing, setClosing] = useState<Position | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; title: string; items: MenuItem[] } | null>(null);
  const bulkButton = useRef<HTMLButtonElement>(null);

  const all = useMemo(() => Object.values(account?.positions ?? {}), [account?.positions]);
  const positions = useMemo(() => sortPositions(all, sort), [all, sort]);
  const totals = useMemo(() => positionTotals(all), [all]);
  const orders = useMemo(() => selectOrders(account?.orders ?? {}), [account?.orders]);
  useQuoteSubscription(orders.map((o) => o.symbol)); // distances need prices even for symbols not in Market Watch
  const digitsOf = (symbol: string) => symbols.data?.find((s) => s.name === symbol)?.digits ?? 5;
  const currency = account?.currency ?? '';
  const online = hubState === 'connected';
  const readOnly = useSession().user?.readOnly === true; // an investor session may look, never act
  const canAct = online && !readOnly;

  const run = async <T,>(id: string, action: () => Promise<Outcome<T>>, done: (value: T) => string) => {
    if (running.current) return false;
    running.current = true;
    setBusy(id);
    setNotice(null);
    const outcome = await action();
    running.current = false;
    setBusy(null);
    setNotice(
      outcome.ok ? { tone: 'ok', text: done(outcome.value) } : { tone: 'error', text: outcome.message },
    );
    return outcome.ok;
  };

  const closeNow = (p: Position) =>
    void run(
      `c${p.ticket}`,
      () => client!.closePosition(p.ticket),
      (r) => `Position #${p.ticket} closed at ${r.price.toFixed(digitsOf(p.symbol))}.`,
    );
  const requestClose = (p: Position) => (confirmClose ? setClosing(p) : closeNow(p));
  const cancelOrder = (o: OrderRow) =>
    void run(
      `x${o.ticket}`,
      () => client!.cancelOrder(o.ticket),
      () => `Order #${o.ticket} cancelled.`,
    );

  /** Does a bulk action on the server, which picks what it takes from its own live figures at that moment. */
  const bulkRun = (b: BulkAction) => {
    const r = b.request;
    if (r.type === 'closeAll') {
      return void run(
        'bulk',
        () => client!.closeAll(r.body),
        (x) =>
          x.outcomes.length === 0
            ? 'Nothing matched any more: no position was closed.'
            : x.closed === x.outcomes.length
              ? `Closed ${x.closed} position${x.closed === 1 ? '' : 's'}.`
              : `Closed ${x.closed} of ${x.outcomes.length}; the rest could not be closed.`,
      );
    }
    if (r.type === 'closeByAll') {
      return void run(
        'bulk',
        () => client!.closeByAll(r.symbol),
        (x) =>
          x.outcomes.length === 0
            ? 'No buy and sell pairs left to close against each other.'
            : x.closed === x.outcomes.length
              ? `Closed ${x.closed} pair${x.closed === 1 ? '' : 's'} against each other.`
              : `Closed ${x.closed} of ${x.outcomes.length} pairs; the rest could not be closed.`,
      );
    }
    return void run(
      'bulk',
      () => client!.cancelAll(r.symbol),
      (x) =>
        x.outcomes.length === 0
          ? 'There were no pending orders left to delete.'
          : x.deleted === x.outcomes.length
            ? `Deleted ${x.deleted} pending order${x.deleted === 1 ? '' : 's'}.`
            : `Deleted ${x.deleted} of ${x.outcomes.length}; the rest could not be deleted.`,
    );
  };

  const asItems = (actions: BulkAction[]): MenuItem[] =>
    actions.map((a) => ({
      key: a.id,
      label: a.label,
      disabled: a.count === 0 || !canAct || busy !== null,
      danger: true,
      onSelect: () => setBulk(a),
    }));

  const closeByNow = (p: Position, other: Position) =>
    void run(
      `cb${p.ticket}`,
      () => client!.closeBy(p.ticket, other.ticket),
      () => `Closed #${p.ticket} against #${other.ticket}: no second spread paid.`,
    );

  const rowMenu = (p: Position, x: number, y: number) =>
    setMenu({
      x,
      y,
      title: `Position #${p.ticket} · ${p.symbol}`,
      items: [
        {
          label: 'Modify, trailing stop or close part…',
          onSelect: () => setEditing({ kind: 'position', row: p }),
        },
        {
          label: 'Close position',
          danger: true,
          disabled: !canAct || busy !== null,
          onSelect: () => requestClose(p),
        },
        ...(p.trailingPoints > 0
          ? [
              {
                key: 'untrail',
                label: `Remove trailing stop (${p.trailingPoints} points)`,
                disabled: !canAct || busy !== null,
                onSelect: () =>
                  void run(
                    `t${p.ticket}`,
                    () => client!.modifyPosition(p.ticket, p.stopLoss, p.takeProfit, 0),
                    () => `Trailing stop removed from #${p.ticket}; the stop loss stays where it is.`,
                  ),
              },
            ]
          : []),
        ...oppositesOf(p, all)
          .slice(0, 4)
          .map((o) => ({
            key: `by${o.ticket}`,
            label: `Close by #${o.ticket} (${o.side.toLowerCase()} ${o.volume})`,
            disabled: !canAct || busy !== null,
            onSelect: () => closeByNow(p, o),
          })),
        { label: `Show ${p.symbol} on the chart`, onSelect: () => select(p.symbol) },
        ...asItems(bulkActions(all, orders, p.symbol)),
      ],
    });

  if (login === undefined) return <p className="p-4 text-sm text-muted">No trading account.</p>;
  if (!account?.loaded) return <p className="p-4 text-sm text-muted">Loading account…</p>;

  return (
    <section className="flex h-full flex-col bg-panel" aria-label="Trade" data-stale={!online}>
      <div className={`min-h-0 flex-1 overflow-auto ${online ? '' : 'opacity-50'}`}>
        {mobile ? (
          <MobileTradeView
            positions={positions}
            orders={orders}
            quotes={quotes}
            digitsOf={digitsOf}
            currency={currency}
            figures={account.figures}
            format={fmt}
            online={canAct}
            busy={busy !== null}
            onModify={(p) => setEditing({ kind: 'position', row: p })}
            onClose={requestClose}
            onModifyOrder={(o) => setEditing({ kind: 'order', row: o })}
            onCancelOrder={cancelOrder}
          />
        ) : (
          <>
            <table className="w-full text-sm" aria-label="Open positions">
              <thead className="sticky top-0 bg-panel">
                <tr className="border-b border-line">
                  {POSITION_COLUMNS.map((c) => (
                    <th
                      key={c.key}
                      scope="col"
                      className={`${th} ${c.right ? 'text-right' : ''}`}
                      aria-sort={
                        sort.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined
                      }
                    >
                      <button
                        type="button"
                        className="uppercase tracking-wide hover:text-fg"
                        onClick={() => setSort(nextSort(sort, c.key))}
                      >
                        {c.key === 'profit' ? `Profit (${currency})` : c.label}
                        <span aria-hidden="true">
                          {sort.key === c.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                        </span>
                      </button>
                    </th>
                  ))}
                  <th className={th} scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {positions.map((p) => {
                  const d = digitsOf(p.symbol);
                  return (
                    <tr
                      key={p.ticket}
                      data-testid="position-row"
                      data-ticket={p.ticket}
                      onClick={() => select(p.symbol)}
                      onDoubleClick={() => setEditing({ kind: 'position', row: p })}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        rowMenu(p, e.clientX, e.clientY);
                      }}
                      title="Double-click to modify, right-click for more"
                      className="cursor-default select-none border-b border-line/40 hover:bg-panel2"
                    >
                      <td className={`${td} num`}>{p.ticket}</td>
                      <td className={`${td} num text-muted`}>
                        {formatServerDateTime(p.timeOpen, zone.offsetAt)}
                      </td>
                      <td className={`${td} font-semibold`}>{p.symbol}</td>
                      <td className={`${td} ${p.side === 'Buy' ? 'text-up' : 'text-down'}`}>
                        {p.side.toLowerCase()}
                      </td>
                      <td className={`${td} num text-right`}>{p.volume}</td>
                      <td className={`${td} num text-right`}>{p.priceOpen.toFixed(d)}</td>
                      <td className={`${td} num text-right`}>
                        {p.stopLoss > 0 ? p.stopLoss.toFixed(d) : '—'}
                        {p.trailingPoints > 0 && (
                          <span
                            className="ml-1 text-accent"
                            title={`Trailing stop: the server keeps the stop loss ${p.trailingPoints} points behind the price`}
                            data-testid="trailing-mark"
                          >
                            ↗<span className="sr-only"> trailing {p.trailingPoints} points</span>
                          </span>
                        )}
                      </td>
                      <td className={`${td} num text-right`}>
                        {p.takeProfit > 0 ? p.takeProfit.toFixed(d) : '—'}
                      </td>
                      <td className={`${td} num text-right`}>
                        {p.price === null ? '—' : p.price.toFixed(d)}
                      </td>
                      <td className={`${td} num text-right`}>{fmt(p.commission)}</td>
                      <td className={`${td} num text-right`}>{fmt(p.swap)}</td>
                      <td
                        className={`${td} num text-right font-semibold ${tone(p.profit)}`}
                        data-testid="position-profit"
                      >
                        {p.profit === null ? '—' : fmt(p.profit)}
                      </td>
                      <td className={`${td} max-w-40 truncate text-muted`} title={p.comment || undefined}>
                        {p.comment ?? ''}
                      </td>
                      <td className={`${td} text-right`}>
                        <button
                          type="button"
                          className={small}
                          onClick={() => setEditing({ kind: 'position', row: p })}
                          aria-label={`Modify position ${p.ticket}`}
                        >
                          Modify
                        </button>{' '}
                        <button
                          type="button"
                          className={small}
                          disabled={!canAct || busy !== null}
                          aria-label={`Close position ${p.ticket}`}
                          onClick={() => requestClose(p)}
                        >
                          Close
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {positions.length === 0 && (
                  <tr>
                    <td colSpan={POSITION_COLUMNS.length + 1} className="px-2 py-3 text-sm text-muted">
                      No open positions.
                    </td>
                  </tr>
                )}
              </tbody>
              {positions.length > 0 && (
                <tfoot className="sticky bottom-0 bg-panel">
                  <tr className="border-t border-line font-semibold" data-testid="position-totals">
                    <th scope="row" colSpan={4} className={`${td} text-left text-xs uppercase text-muted`}>
                      Total ({positions.length})
                    </th>
                    <td className={`${td} num text-right`} data-testid="total-volume">
                      {totals.volume}
                    </td>
                    <td colSpan={4} />
                    <td className={`${td} num text-right`} data-testid="total-commission">
                      {fmt(totals.commission)}
                    </td>
                    <td className={`${td} num text-right`} data-testid="total-swap">
                      {fmt(totals.swap)}
                    </td>
                    <td className={`${td} num text-right ${tone(totals.profit)}`} data-testid="total-profit">
                      {totals.profit === null ? '—' : fmt(totals.profit)}
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              )}
            </table>

            <table className="mt-3 w-full text-sm" aria-label="Pending orders">
              <thead>
                <tr className="border-y border-line">
                  {[
                    'Ticket',
                    'Symbol',
                    'Type',
                    'Volume',
                    'Price',
                    'Distance',
                    'S/L',
                    'T/P',
                    'Expires',
                    '',
                  ].map((h, i) => (
                    <th key={i} className={`${th} ${i >= 3 && i <= 7 ? 'text-right' : ''}`} scope="col">
                      {h === 'Distance' ? (
                        <span title="How far the market is from triggering the order">Distance (pts)</span>
                      ) : (
                        h || <span className="sr-only">Actions</span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => {
                  const d = digitsOf(o.symbol);
                  const distance = pendingDistance(o, quotes[o.symbol], d);
                  return (
                    <tr
                      key={o.ticket}
                      data-testid="order-row"
                      data-ticket={o.ticket}
                      onClick={() => select(o.symbol)}
                      onDoubleClick={() => setEditing({ kind: 'order', row: o })}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setMenu({
                          x: e.clientX,
                          y: e.clientY,
                          title: `Order #${o.ticket} · ${o.symbol}`,
                          items: [
                            { label: 'Modify…', onSelect: () => setEditing({ kind: 'order', row: o }) },
                            {
                              label: 'Cancel order',
                              danger: true,
                              disabled: !canAct || busy !== null,
                              onSelect: () => cancelOrder(o),
                            },
                            { label: `Show ${o.symbol} on the chart`, onSelect: () => select(o.symbol) },
                          ],
                        });
                      }}
                      title="Double-click to modify, right-click for more"
                      className="cursor-default select-none border-b border-line/40 hover:bg-panel2"
                    >
                      <td className={`${td} num`}>{o.ticket}</td>
                      <td className={`${td} font-semibold`}>{o.symbol}</td>
                      <td className={td}>{o.type.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()}</td>
                      <td className={`${td} num text-right`}>{o.volumeCurrent}</td>
                      <td className={`${td} num text-right`}>
                        {o.priceOrder.toFixed(d)}
                        {o.priceStopLimit > 0 ? ` / ${o.priceStopLimit.toFixed(d)}` : ''}
                      </td>
                      <td className={`${td} num text-right`} data-testid="order-distance">
                        {distance === null ? '—' : distance}
                      </td>
                      <td className={`${td} num text-right`}>
                        {o.stopLoss > 0 ? o.stopLoss.toFixed(d) : '—'}
                      </td>
                      <td className={`${td} num text-right`}>
                        {o.takeProfit > 0 ? o.takeProfit.toFixed(d) : '—'}
                      </td>
                      <td className={td}>
                        {o.expiration === 'Specified' && o.expirationTime
                          ? formatServerDateTime(o.expirationTime, zone.offsetAt).slice(0, 16)
                          : o.expiration === 'Gtc'
                            ? 'GTC'
                            : o.expiration}
                      </td>
                      <td className={`${td} text-right`}>
                        <button
                          type="button"
                          className={small}
                          onClick={() => setEditing({ kind: 'order', row: o })}
                          aria-label={`Modify order ${o.ticket}`}
                        >
                          Modify
                        </button>{' '}
                        <button
                          type="button"
                          className={small}
                          disabled={!canAct || busy !== null}
                          aria-label={`Cancel order ${o.ticket}`}
                          onClick={() => cancelOrder(o)}
                        >
                          Cancel
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {orders.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-2 py-3 text-sm text-muted">
                      No pending orders.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </>
        )}
      </div>

      <div className="flex items-center gap-3 border-t border-line px-3 py-2 text-sm">
        <span className="text-muted">
          {positions.length} position{positions.length === 1 ? '' : 's'} · {orders.length} order
          {orders.length === 1 ? '' : 's'}
        </span>
        <div className="min-w-0 flex-1" aria-live="polite">
          {notice && (
            <span
              role={notice.tone === 'error' ? 'alert' : 'status'}
              className={notice.tone === 'error' ? 'text-down' : 'text-up'}
              data-testid="trade-notice"
            >
              {notice.text}
            </span>
          )}
        </div>
        {bulk ? (
          <>
            <span className="text-warn">{bulk.question}</span>
            <button
              type="button"
              className={`${small} border-down text-down`}
              disabled={!canAct || busy !== null}
              onClick={() => {
                setBulk(null);
                bulkRun(bulk);
              }}
            >
              {bulk.confirmLabel}
            </button>
            <button type="button" className={small} onClick={() => setBulk(null)}>
              Keep
            </button>
          </>
        ) : (
          <button
            ref={bulkButton}
            type="button"
            className={small}
            aria-haspopup="menu"
            disabled={!canAct || (positions.length === 0 && orders.length === 0) || busy !== null}
            onClick={() => {
              const box = bulkButton.current!.getBoundingClientRect();
              const actions = bulkActions(all, orders);
              setMenu({
                x: box.left,
                y: box.top - 8 - 32 * (actions.length + 1),
                title: 'Close or delete',
                items: asItems(actions),
              });
            }}
          >
            Close or delete…
          </button>
        )}
      </div>

      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          title={menu.title}
          items={menu.items}
          onClose={() => setMenu(null)}
        />
      )}
      <ConfirmDialog
        open={closing !== null}
        title={closing ? `Close position #${closing.ticket}?` : ''}
        confirmLabel="Close position"
        onCancel={() => setClosing(null)}
        onConfirm={() => {
          if (closing) closeNow(closing);
          setClosing(null);
        }}
      >
        {closing && (
          <p>
            {closing.side} {closing.volume} {closing.symbol} at the market price. Profit now:{' '}
            <span className={`num ${tone(closing.profit)}`}>
              {closing.profit === null ? '—' : `${signed(closing.profit)} ${currency}`}
            </span>
            . You can turn this question off in Settings.
          </p>
        )}
      </ConfirmDialog>
      <EditDialog
        editing={editing}
        digits={editing ? digitsOf(editing.row.symbol) : 5}
        info={editing ? symbols.data?.find((s) => s.name === editing.row.symbol) : undefined}
        online={canAct}
        balance={account.figures.balance}
        currency={currency}
        onClose={() => setEditing(null)}
        onResult={(tone, text) => setNotice({ tone, text })}
        client={client}
      />
    </section>
  );
}

interface EditProps {
  editing: Editing;
  digits: number;
  info: { volumeMin: number; volumeMax: number; volumeStep: number; stopsLevel?: number } | undefined;
  online: boolean;
  balance: number;
  currency: string;
  onClose: () => void;
  onResult: (tone: 'ok' | 'error', text: string) => void;
  client: ReturnType<typeof useTradeClient>;
  /** A classic-terminal title, e.g. "Position: #123 sell 0.01 EURUSD 1.13282"; the default is used when omitted. */
  title?: string;
  /** Closes the position at the market, right from this dialog (the classic terminal's "Close by Market"). */
  onCloseNow?: () => void;
  /** Cancels the pending order right from this dialog (the classic terminal's "Cancel order"). */
  onCancelOrder?: () => void;
  /** Shows a live tick chart of the symbol next to the form, as the classic terminal's own popups do. */
  showChart?: boolean;
}

export function EditDialog(props: EditProps) {
  const { editing, client, onClose, title, showChart } = props;
  return (
    <Modal
      size={showChart ? 'xl' : 'md'}
      open={editing !== null}
      title={
        title ??
        (editing
          ? `${editing.kind === 'position' ? 'Position' : 'Order'} #${editing.row.ticket} · ${editing.row.symbol}`
          : '')
      }
      onClose={onClose}
    >
      {editing && client && showChart && (
        <div className="flex gap-4">
          <div className="min-w-0 flex-1">
            <EditForm key={editing.row.ticket} {...props} editing={editing} client={client} />
          </div>
          <div className="hidden h-96 w-80 shrink-0 border border-line md:block">
            <TickChart />
          </div>
        </div>
      )}
      {editing && client && !showChart && (
        <EditForm key={editing.row.ticket} {...props} editing={editing} client={client} />
      )}
    </Modal>
  );
}

function EditForm({
  editing,
  digits,
  info,
  online,
  balance,
  currency,
  onClose,
  onResult,
  client,
  onCloseNow,
  onCancelOrder,
}: EditProps & { editing: NonNullable<Editing>; client: NonNullable<EditProps['client']> }) {
  const row = editing.row;
  const isPosition = editing.kind === 'position';
  const side: Side = isPosition ? (editing.row.side as Side) : sideOfOrderType(editing.row.type as OrderType);
  const volume = isPosition ? editing.row.volume : editing.row.volumeCurrent;
  const quote = useStream((s) => s.quotes[row.symbol]);
  const moneyPerLot = useMoneyPerLot(row.symbol, digits);
  const fmt = useMoneyFormat();
  const signed = (v: number) => `${v > 0 ? '+' : ''}${fmt(v)}`;

  const [unit, setUnit] = useState<LevelUnit>('price');
  const [stopLoss, setStopLoss] = useState(row.stopLoss > 0 ? row.stopLoss.toFixed(digits) : '');
  const [takeProfit, setTakeProfit] = useState(row.takeProfit > 0 ? row.takeProfit.toFixed(digits) : '');
  const [trailing, setTrailing] = useState(
    isPosition && editing.row.trailingPoints > 0 ? String(editing.row.trailingPoints) : '',
  );
  const [price, setPrice] = useState(isPosition ? '' : editing.row.priceOrder.toFixed(digits));
  const [closeVolume, setCloseVolume] = useState(isPosition ? String(editing.row.volume) : '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const step = String(1 / 10 ** digits);
  const label = 'mb-1 block text-xs uppercase tracking-wide text-muted';

  // Distances are measured from the open price of a position, and from the (possibly edited) price of an order.
  const entry = isPosition ? editing.row.priceOpen : Number(price);
  const ctx = {
    side,
    entry,
    digits,
    moneyPerPrice: moneyPerLot === null ? null : moneyPerLot * volume,
    balance,
  };
  /** The level as a price: 0 when empty, null when it cannot be understood (yet). */
  const resolve = (kind: LevelKind): number | null => {
    const text = kind === 'stopLoss' ? stopLoss : takeProfit;
    if (text.trim() === '') return 0;
    if (!(entry > 0)) return null;
    return levelPrice(unit, text, kind, ctx);
  };
  const sl = resolve('stopLoss');
  const tp = resolve('takeProfit');
  // A position closes at the bid (buy) or ask (sell); an order is triggered by the ask (buy) or bid (sell).
  const market = quote
    ? isPosition === (side === 'Buy')
      ? { price: quote.bid, name: 'bid' }
      : { price: quote.ask, name: 'ask' }
    : null;
  const outcome = (level: number | null) =>
    level && level > 0 && moneyPerLot !== null && entry > 0
      ? resultAt(side, entry, level, volume, moneyPerLot)
      : null;

  const switchUnit = (next: LevelUnit) => {
    const converted = (['stopLoss', 'takeProfit'] as const).map((kind) => {
      const level = resolve(kind);
      return level === null ? null : levelText(next, level, kind, ctx);
    });
    if (converted.some((c) => c === null)) {
      return setError(
        next === 'money' || next === 'percent'
          ? 'Money values are still loading, or a level is beyond the entry price; it can only be edited as a price.'
          : 'A level is beyond the entry price (a stop loss in profit, say), so it can only be edited as a price.',
      );
    }
    setError(null);
    setUnit(next);
    setStopLoss(converted[0]!);
    setTakeProfit(converted[1]!);
  };

  const finish = (outcome: Outcome<unknown>, success: string) => {
    setBusy(false);
    if (outcome.ok) {
      onResult('ok', success);
      onClose();
    } else {
      setError(outcome.message);
    }
  };

  const save = async () => {
    if (!isPosition && !(Number(price) > 0)) return setError('Enter a price.');
    if (sl === null || tp === null) {
      return setError(
        `Enter the stop loss and take profit as numbers in ${UNIT_LABELS[unit].toLowerCase()}.`,
      );
    }
    const trail = trailing.trim() === '' ? 0 : Number(trailing);
    if (isPosition) {
      const least = info?.stopsLevel ?? 0;
      if (!Number.isInteger(trail) || trail < 0)
        return setError('The trailing stop is a whole number of points, or empty for none.');
      if (trail > 0 && trail < least)
        return setError(`The trailing stop must be at least ${least} points for ${row.symbol}.`);
    }
    setBusy(true);
    setError(null);
    if (editing.kind === 'position') {
      finish(await client.modifyPosition(row.ticket, sl, tp, trail), `Position #${row.ticket} updated.`);
    } else {
      finish(
        await client.modifyOrderFrom(editing.row, { price: Number(price), stopLoss: sl, takeProfit: tp }),
        `Order #${row.ticket} updated.`,
      );
    }
  };

  const closePartly = async () => {
    if (editing.kind !== 'position' || !info) return;
    const v = Number(closeVolume);
    const problem = volumeProblem(v, info);
    if (problem) return setError(problem);
    if (v > editing.row.volume) return setError('That is more than the position holds.');
    setBusy(true);
    setError(null);
    const closing = normalizeVolume(v, info);
    finish(
      await client.closePosition(row.ticket, closing >= editing.row.volume ? undefined : closing),
      `Closed ${closing} of position #${row.ticket}.`,
    );
  };

  const levelField = (kind: LevelKind, id: string, name: string, value: string, set: (v: string) => void) => {
    const level = kind === 'stopLoss' ? sl : tp;
    const result = outcome(level);
    // The points field mirrors the price field (either edits the other) when SL/TP is entered as a price, as MT5 shows
    // both at once; it's hidden when another unit is picked, since the main field already is that distance.
    const points =
      unit === 'price' && level && level > 0 ? (levelText('points', level, kind, ctx) ?? '') : '';
    const setFromPoints = (text: string) => {
      if (text.trim() === '') return set('');
      if (!(entry > 0)) return;
      const price = levelPrice('points', text, kind, ctx);
      if (price !== null && price > 0) set(price.toFixed(digits));
    };
    return (
      <div>
        <div className="flex items-center gap-2">
          <label className="w-20 shrink-0 text-xs uppercase tracking-wide text-muted" htmlFor={id}>
            {name}
          </label>
          <input
            id={id}
            type="number"
            step={unit === 'price' ? step : 'any'}
            className={`${fieldBase} min-w-0 flex-1`}
            placeholder="none"
            value={value}
            onChange={(e) => set(e.target.value)}
            aria-describedby={`${id}-result`}
          />
          {unit === 'price' && (
            <>
              <input
                type="number"
                className={`${fieldBase} w-20 shrink-0`}
                placeholder="0"
                value={points}
                onChange={(e) => setFromPoints(e.target.value)}
                aria-label={`${name}, in points`}
              />
              <span className="shrink-0 text-xs text-muted">points</span>
            </>
          )}
        </div>
        <p
          id={`${id}-result`}
          className="num ml-[5.5rem] mt-1 min-h-[1rem] text-xs text-muted"
          data-testid={`${id}-result`}
        >
          {level === null
            ? '…'
            : level === 0
              ? ''
              : `${unit === 'price' ? '' : `${level.toFixed(digits)} · `}${
                  result === null ? '' : `≈ ${signed(result)} ${currency}`
                }`}
        </p>
      </div>
    );
  };

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
      noValidate
    >
      <p className="num rounded-md bg-panel2 px-2 py-1 text-xs" data-testid="edit-market">
        {side} {volume} · {isPosition ? `open ${entry.toFixed(digits)}` : 'pending'} · now{' '}
        {market ? `${market.price.toFixed(digits)} (${market.name})` : '—'}
        {isPosition && editing.row.profit !== null && (
          <>
            {' '}
            · profit{' '}
            <span className={tone(editing.row.profit)}>
              {signed(editing.row.profit)} {currency}
            </span>
          </>
        )}
      </p>
      {editing.kind === 'order' && (
        <div>
          <label className={label} htmlFor="edit-price">
            Price
          </label>
          <input
            id="edit-price"
            type="number"
            step={step}
            className={input}
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
        </div>
      )}
      <div className="flex flex-col gap-2">
        {levelField('stopLoss', 'edit-sl', 'Stop loss', stopLoss, setStopLoss)}
        {levelField('takeProfit', 'edit-tp', 'Take profit', takeProfit, setTakeProfit)}
      </div>
      <div>
        <label className={label} htmlFor="edit-unit">
          SL / TP entered as
        </label>
        <select
          id="edit-unit"
          className={input}
          value={unit}
          onChange={(e) => switchUnit(e.target.value as LevelUnit)}
        >
          {LEVEL_UNITS.map((u) => (
            <option key={u} value={u}>
              {UNIT_LABELS[u]}
            </option>
          ))}
        </select>
        {unit !== 'price' && (
          <p className="mt-1 text-xs text-muted">
            Measured from the {isPosition ? 'open price' : 'order price'}.
          </p>
        )}
      </div>
      {editing.kind === 'position' && (
        <div>
          <label className={label} htmlFor="edit-trail">
            Trailing stop (points)
          </label>
          <input
            id="edit-trail"
            inputMode="numeric"
            className={input}
            placeholder="off"
            value={trailing}
            onChange={(e) => setTrailing(e.target.value)}
            aria-describedby="edit-trail-hint"
          />
          <p id="edit-trail-hint" className="mt-1 text-xs text-muted">
            Once the price is this far in profit, the stop loss follows it at this distance and never moves
            back. It runs on the server, so it keeps working when the terminal is closed.
            {info?.stopsLevel ? ` Minimum ${info.stopsLevel} points.` : ''}
          </p>
        </div>
      )}
      {editing.kind === 'position' && (
        <div>
          <label className={label} htmlFor="edit-close-volume">
            Volume to close
          </label>
          <div className="flex gap-2">
            <input
              id="edit-close-volume"
              className={input}
              inputMode="decimal"
              value={closeVolume}
              onChange={(e) => setCloseVolume(e.target.value)}
            />
            <button
              type="button"
              className={`${small} whitespace-nowrap`}
              disabled={!online || busy}
              onClick={() => void closePartly()}
            >
              Close volume
            </button>
          </div>
        </div>
      )}
      <p role="alert" className="min-h-[1.25rem] text-sm text-down">
        {error}
      </p>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          {isPosition && onCloseNow && (
            <button
              type="button"
              disabled={!online || busy}
              onClick={() => {
                onCloseNow();
                onClose();
              }}
              className="rounded-md bg-down px-3 py-1.5 text-sm font-semibold text-black transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Close by Market
            </button>
          )}
          {!isPosition && onCancelOrder && (
            <button
              type="button"
              disabled={!online || busy}
              onClick={() => {
                onCancelOrder();
                onClose();
              }}
              className="rounded-md bg-down px-3 py-1.5 text-sm font-semibold text-black transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Cancel order
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <button type="button" className={small} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={!online || busy}
            className="rounded-md bg-accent px-3 py-1 text-sm font-semibold text-accent-fg disabled:opacity-40"
          >
            Modify
          </button>
        </div>
      </div>
    </form>
  );
}
